// User profile component for Studio1 Alpha
// Displays user information and sign out functionality

import React, { useState, useEffect } from "react";
import {
  LogOut,
  Settings,
  ChevronDown,
  Bug,
  Building2,
  Activity,
  MessageSquare,
  LifeBuoy,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "../../stores/appStore";
import { logger } from "../../services/logger";
import { generateUUID } from "../../utils/uuid";
import {
  LanguageSwitcher,
  hasMultipleLanguages,
} from "../i18n/LanguageSwitcher";
import { routes } from "@/routes";
import { ActivityDrawer } from "./ActivityDrawer";
import { ContactSupportDialog } from "../support/ContactSupportDialog";

interface UserProfileProps {
  className?: string;
}

export const UserProfile: React.FC<UserProfileProps> = ({ className = "" }) => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { organisation } = useAppStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // Reset avatar error when user changes or avatar URL changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.profilePictureUrl]);

  const handleSignOut = () => {
    const correlationId = generateUUID();

    logger.info("User initiated sign out", {
      userId: user?.id,
      correlationId,
    });

    setIsDropdownOpen(false);
    signOut(); // Redirects to backend /auth/logout
  };

  if (!user) {
    return null;
  }

  // Before the user accepts the Terms of Service the app is gated. Hide the
  // navigation/action menu items so they can't reach gated areas from the
  // ToS page; "Sign out" stays available. Fail open when the flag is absent.
  const tosAccepted = user.hasAcceptedTos !== false;

  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName || user.email?.split("@")[0] || "User";

  const initials = displayName
    .split(" ")
    .map((name: string) => name.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-3 p-2 transition-all hover:bg-accent/30"
        style={{ borderRadius: "var(--radius-default)" }}
      >
        {/* User Avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          {user.profilePictureUrl && !avatarError ? (
            <img
              src={user.profilePictureUrl}
              alt={displayName}
              className="w-8 h-8 rounded-full object-cover"
              onError={() => {
                logger.warn("Failed to load user avatar", {
                  userId: user?.id,
                  avatarUrl: user.profilePictureUrl,
                });
                setAvatarError(true);
              }}
              onLoad={() => setAvatarError(false)}
            />
          ) : (
            <span className="text-white text-sm font-medium">{initials}</span>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1 text-left">
          <p className="text-sm truncate text-muted-foreground">{user.email}</p>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div
          className="absolute right-0 mt-3 w-56 z-50"
          style={{
            backgroundColor: "var(--color-panel)",
            border: "1px solid hsl(var(--border))",
            borderRadius: "var(--radius-default)",
            boxShadow:
              "0 4px 6px rgba(var(--color-primitive-neutral-1000-rgb),0.3)",
            padding: "4px",
          }}
        >
          <div className="px-3 py-2 text-left">
            <p className="text-sm font-medium text-foreground text-left">
              {displayName}
            </p>
            <p className="text-xs text-muted-foreground text-left">
              {user.email}
            </p>
          </div>

          <div className="py-1">
            {tosAccepted && (
              <>
                {organisation && (
                  <button
                    className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-accent/30 transition-all"
                    style={{ borderRadius: "6px", fontSize: "13px" }}
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate(routes.organisationMembers(organisation.id));
                    }}
                  >
                    <Building2 className="w-4 h-4 mr-3 text-muted-foreground" />
                    {organisation.name}
                  </button>
                )}

                <button
                  className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-accent/30 transition-all"
                  style={{ borderRadius: "6px", fontSize: "13px" }}
                  onClick={() => {
                    setIsDropdownOpen(false);
                    setIsActivityDrawerOpen(true);
                  }}
                >
                  <Activity className="w-4 h-4 mr-3 text-muted-foreground" />
                  Activity
                </button>

                <button
                  className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-accent/30 transition-all"
                  style={{ borderRadius: "6px", fontSize: "13px" }}
                  onClick={() => {
                    setIsDropdownOpen(false);
                    setIsContactDialogOpen(true);
                  }}
                >
                  <MessageSquare className="w-4 h-4 mr-3 text-muted-foreground" />
                  Contact Support
                </button>

                <button
                  className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-accent/30 transition-all"
                  style={{ borderRadius: "6px", fontSize: "13px" }}
                  onClick={() => {
                    setIsDropdownOpen(false);
                    navigate(routes.support());
                  }}
                >
                  <LifeBuoy className="w-4 h-4 mr-3 text-muted-foreground" />
                  My Support
                </button>

                <button
                  className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-accent/30 transition-all"
                  style={{ borderRadius: "6px", fontSize: "13px" }}
                  onClick={() => {
                    setIsDropdownOpen(false);
                    navigate(routes.settings());
                  }}
                >
                  <Settings className="w-4 h-4 mr-3 text-muted-foreground" />
                  {t("navigation.settings")}
                </button>

                {/* Debug link - only in development */}
                {import.meta.env.DEV && (
                  <button
                    className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-accent/30 transition-all"
                    style={{ borderRadius: "6px", fontSize: "13px" }}
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate(routes.authDebug());
                    }}
                  >
                    <Bug className="w-4 h-4 mr-3 text-muted-foreground" />
                    {t("debug.authDebug")}
                  </button>
                )}

                {/* Language (billing account selector moved to the header) */}
                {hasMultipleLanguages() && (
                  <>
                    <div className="-mx-1 border-t border-[hsl(var(--border))] mt-1" />
                    <div className="px-3 py-2 space-y-2">
                      <LanguageSwitcher />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="-mx-1 border-t border-[hsl(var(--border))] mt-2" />
            <div className="px-1 pt-2">
              <button
                onClick={handleSignOut}
                className="flex items-center justify-center w-full transition-all"
                style={{
                  height: "36px",
                  padding: "0 16px",
                  gap: "8px",
                  borderRadius: "var(--radius-default)",
                  fontSize: "14px",
                  fontWeight: 500,
                  background: "rgba(var(--color-primitive-red-500-rgb),0.15)",
                  border:
                    "1px solid rgba(var(--color-primitive-red-500-rgb),0.4)",
                  color: "var(--color-primitive-red-500)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "rgba(var(--color-primitive-red-500-rgb),0.25)";
                  e.currentTarget.style.borderColor =
                    "rgba(var(--color-primitive-red-500-rgb),0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "rgba(var(--color-primitive-red-500-rgb),0.15)";
                  e.currentTarget.style.borderColor =
                    "rgba(var(--color-primitive-red-500-rgb),0.4)";
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = "scale(0.97)";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <LogOut className="w-4 h-4" />
                {t("auth.signOut")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay to close dropdown */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      <ActivityDrawer
        open={isActivityDrawerOpen}
        onOpenChange={setIsActivityDrawerOpen}
      />

      <ContactSupportDialog
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
      />
    </div>
  );
};
